/* global SideDrawer */

class CartDrawer extends SideDrawer {
    connectedCallback() {
        this.bindEvents();
    }

    bindEvents() {
        this.cartRefreshHandler = this.cartRefreshHandler.bind(this);
        this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
        this.closeDrawerViaEventHandler = this.close.bind(this, null);

        document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
        document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
        document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);

        this.addEventListener('on:cart-drawer:before-open', () => {
            theme.manuallyLoadImages(this);
            this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
            this.applyAutoDiscount();
        });

        this.addEventListener('on:cart:after-merge', () => {
            theme.manuallyLoadImages(this);
            this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
            this.applyAutoDiscount();
        });
    }

    disconnectedCallback() {
        document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
        document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
        document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    }

    handleDrawerOpenViaEvent(evt) {
        this.open(evt.detail ? evt.detail.opener : null);
    }

    cartRefreshHandler() {
        this.querySelector('cart-form').refresh();
    }

    updateFromCartChange(html) {
        this.querySelector('cart-form').refreshFromHtml(html);
    }

    /**
     * Add the configured code while keeping codes already applied to the cart.
     */
    async applyAutoDiscount() {
        if (this.dataset.autoDiscountEnabled !== 'true') return;

        const code = (this.dataset.autoDiscountCode || '').trim();
        if (!code || this.autoDiscountInFlight) return;

        // Don't interrupt someone who is using the discount popup.
        if (this.querySelector('.drawer-discounts__dialog[open]')) return;

        this.autoDiscountInFlight = true;

        try {
            const root = window.Shopify?.routes?.root || '/';

            const cartResponse = await fetch(root + 'cart.js', {
                cache: 'no-store'
            });

            if (!cartResponse.ok) throw new Error('Could not read cart');

            const cart = await cartResponse.json();
            if (!cart.item_count) return;

            // This is the data-codes attribute in your existing discount markup.
            const discountBox = this.querySelector('.drawer-discounts');

            // The drawer may still be rendering after the first add.
            // The after-merge event will try again once the markup is ready.
            if (!discountBox) return;

            const appliedCodes = JSON.parse(discountBox.dataset.codes || '[]');

            if (
                appliedCodes.some(
                    existing => existing.toLowerCase() === code.toLowerCase()
                )
            ) {
                return;
            }

            // Prevent an endless refresh loop if the code isn't eligible.
            // A new item or quantity change creates a new cart state.
            const cartState = JSON.stringify(
                cart.items.map(item => [
                    item.id,
                    item.quantity,
                    item.properties
                ])
            );

            const attemptKey =
                `${cart.token}|${cartState}|${code.toLowerCase()}`;

            if (this.lastAutoDiscountAttempt === attemptKey) return;
            this.lastAutoDiscountAttempt = attemptKey;

            const response = await fetch(root + 'cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    discount: [...appliedCodes, code].join(',')
                })
            });

            if (!response.ok) throw new Error('Could not apply automatic discount');

            document.dispatchEvent(
                new CustomEvent('dispatch:cart-drawer:refresh')
            );
        } catch (error) {
            // Allow a later drawer opening to retry a network failure.
            this.lastAutoDiscountAttempt = null;
            console.error('Automatic cart discount:', error);
        } finally {
            this.autoDiscountInFlight = false;
        }
    }
}

window.customElements.define('cart-drawer', CartDrawer);

/** Cart Drawer Discount Code Script */
if (!window.drawerDiscountModalHandlerAdded) {
    window.drawerDiscountModalHandlerAdded = true;

    async function updateDiscountCodes(box, codes, attemptedCode = null) {
        const dialog = box.querySelector('.drawer-discounts__dialog');
        const input = dialog.querySelector('input');
        const error = dialog.querySelector('.drawer-discounts__error');
        const status = box.querySelector('.drawer-discounts__status');
        const buttons = box.querySelectorAll('button');
        const drawer = box.closest('cart-drawer');
        const sectionId = drawer.dataset.sectionId;

        buttons.forEach(button => button.disabled = true);
        input.classList.remove('is-invalid');
        error.textContent = '';
        status.textContent = '';

        try {
            const response = await fetch(
                (window.Shopify?.routes?.root || '/') + 'cart/update.js',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        discount: codes.join(','),
                        sections: sectionId
                    })
                }
            );

            if (!response.ok) throw new Error('Cart update failed');

            const cart = await response.json();
            const sectionHTML = cart.sections?.[sectionId];
            if (!sectionHTML) throw new Error('Cart section was not returned');

            const updatedBox = new DOMParser()
                .parseFromString(sectionHTML, 'text/html')
                .querySelector('.drawer-discounts');

            if (!updatedBox) throw new Error('Discount section was not returned');

            const appliedCodes = JSON.parse(updatedBox.dataset.codes || '[]');

            if (
                attemptedCode &&
                !appliedCodes.some(
                    code => code.toLowerCase() === attemptedCode.toLowerCase()
                )
            ) {
                input.classList.add('is-invalid');
                error.textContent = 'Enter a valid discount code';
                return;
            }

            if (dialog.open) dialog.close();

            document.dispatchEvent(
                new CustomEvent('dispatch:cart-drawer:refresh')
            );
        } catch (err) {
            const message = 'Could not update discounts. Please try again.';

            if (dialog.open) {
                error.textContent = message;
            } else {
                status.textContent = message;
            }
        } finally {
            buttons.forEach(button => button.disabled = false);
        }
    }

    document.addEventListener('click', event => {
        const openButton = event.target.closest('[data-discount-open]');
        const closeButton = event.target.closest('[data-discount-close]');
        const applyButton = event.target.closest('[data-discount-apply]');
        const removeButton = event.target.closest('[data-discount-remove]');
        const button = openButton || closeButton || applyButton || removeButton;

        if (!button || button.disabled) return;

        const box = button.closest('.drawer-discounts');
        const dialog = box.querySelector('.drawer-discounts__dialog');

        if (openButton) {
            const drawerRect = box.closest('cart-drawer').getBoundingClientRect();

            dialog.style.setProperty(
                '--discount-dialog-x',
                `${drawerRect.left + drawerRect.width / 2}px`
            );

            dialog.style.setProperty(
                '--discount-dialog-y',
                `${drawerRect.top + drawerRect.height / 2}px`
            );

            const input = dialog.querySelector('input');
            input.value = '';
            input.classList.remove('is-invalid');
            dialog.querySelector('.drawer-discounts__error').textContent = '';
            dialog.showModal();
            return;
        }

        if (closeButton) {
            dialog.close();
            return;
        }

        const codes = JSON.parse(box.dataset.codes || '[]');

        if (applyButton) {
            const input = dialog.querySelector('input');
            const code = input.value.trim();

            if (!code) {
                input.classList.add('is-invalid');
                dialog.querySelector('.drawer-discounts__error').textContent =
                    'Enter a discount code.';
                return;
            }

            if (
                codes.some(
                    existing => existing.toLowerCase() === code.toLowerCase()
                )
            ) {
                dialog.querySelector('.drawer-discounts__error').textContent =
                    'Code already applied.';
                return;
            }

            codes.push(code);
            updateDiscountCodes(box, codes, code);
        }

        if (removeButton) {
            const removedCode =
                removeButton.dataset.discountRemove.toLowerCase();

            const remainingCodes = codes.filter(
                code => code.toLowerCase() !== removedCode
            );

            updateDiscountCodes(box, remainingCodes);
        }
    });

    document.addEventListener('keydown', event => {
        if (
            event.key === 'Enter' &&
            event.target.matches('.drawer-discounts__dialog input')
        ) {
            event.preventDefault();
            event.target
                .closest('.drawer-discounts__dialog')
                .querySelector('[data-discount-apply]')
                .click();
        }
    });
}